import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { Sentry } from '@/lib/sentry';

function Fallback({ error, resetErrorBoundary }: FallbackProps) {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="flex min-h-screen flex-col items-center justify-center gap-6 bg-cream-50 px-6 text-center text-ink-700 dark:bg-midnight-950 dark:text-warm-100"
    >
      <h1 className="font-serif text-2xl">{t('errors.somethingWentWrong', 'Something went wrong')}</h1>
      <p className="max-w-md text-sm text-ink-500 dark:text-warm-300">
        {error?.message ?? t('errors.unknown', 'An unexpected error occurred.')}
      </p>
      <button
        type="button"
        onClick={resetErrorBoundary}
        className="cursor-pointer rounded-2xl bg-jade-500 px-6 py-3 font-medium text-white shadow-glow-jade-soft transition-colors duration-200 hover:bg-jade-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jade-500 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 dark:focus-visible:ring-offset-midnight-950"
      >
        {t('errors.reload', 'Reload')}
      </button>
    </div>
  );
}

export function RootErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary
      FallbackComponent={Fallback}
      onError={(error, info) => {
        Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
      }}
      onReset={() => {
        // Best-effort recovery: a hard reload usually clears the bad state.
        window.location.reload();
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
